import { PlanConfirmationModal as PlanConfirmationModalComponent, ProcessingScreen } from '@components'

import { notify, SkeletonBox, useModal, withModalConditionalRender } from '@billy/design-system'
import isEmpty from 'lodash/isEmpty'
import qs from 'qs'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Navigate, useNavigate } from 'react-router'
import { useLocation } from 'react-use'

import { useZervantSubscriptionDetails } from '@features/zervant-subscription/hooks/useZervantSubscriptionDetails'
import { BillingType } from '@modules-deprecated/app/organization/enums/billingType'
import { SubscriptionPeriod } from '@modules-deprecated/app/organization/enums/subscriptionPeriod'
import { SubscriptionPlan } from '@modules-deprecated/app/organization/enums/subscriptionPlan'

import { ModalId } from '../../../../enums/modalId'
import { NotificationKeys } from '../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../enums/queryKeys'
import { useOrganizationSubscription, useSegment } from '../../../../hooks'
import { useOrganizationUiSetup } from '../../../../hooks/useOrganizationUiSetup'
import { isValidSubscriptionPlan } from '../../../../utils/isValidSubscriptionPlan'
import { SettingsRoute } from '../../enums/settingsRoute'
import { useCouponEligibility } from '../../hooks/useCouponEligibility'
import { useCouponParams } from '../../hooks/useCouponParams'
import { useOrganizationSubscriptionSettings } from './contexts/organizationSubscriptionSettingsContext'
import { ActivateAlertComponent } from './elements/ActivateAlertComponent'
import { SubscriptionPeriodChangeModal } from './elements/SectionPlanDetailsComponent/elements/SubscriptionPeriodChangeModal'
import { StripePortalSubscription } from './elements/StripePortalSubscription'
import { SubscriptionOverview } from './elements/SubscriptionOverview'
import { SubscriptionPlansOverlay } from './elements/SubscriptionPlansOverlay'
import { useShowStripePortalSubscription } from './hooks/useShowStripePortalSubscription'
import { useSubscriptionPlanPrices } from './hooks/useSubscriptionPlanPrices'
import { isPaidPlan } from './plansData'
import { changeSubscriptionPeriod, fetchSubscription, updateBillingSubscription } from './services/query-api'
import { UpdateSubscriptionMutationParams } from './types'
import { getUpdateSubscriptionPayload, notifySubscriptionUpdateError, notifySubscriptionUpdateSuccess } from './utils'

const PlanConfirmationModal = withModalConditionalRender(PlanConfirmationModalComponent)

function SubscriptionComponent(): ReactElement | null {
  const { isUiSetupSupernavInvoicingAccounting } = useOrganizationUiSetup()
  const { isLoading: isStripePortalLoading, showStripePortal } = useShowStripePortalSubscription()

  if (isUiSetupSupernavInvoicingAccounting) {
    return <Navigate to={SettingsRoute.Organization} replace />
  }

  if (showStripePortal || isStripePortalLoading) {
    return <StripePortalSubscription isLoading={isStripePortalLoading} />
  }

  return <SubscriptionContent />
}

function SubscriptionContent(): ReactElement | null {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const {
    isLoading: isSubscriptionDataLoading,
    planExpirationDate,
    subscriptionPlan,
    subscriptionPeriod,
    isTrialNoPlanSelected,
    isSubscriptionUpdating,
    billingType,
    defaultActivePaymentMethod,
  } = useOrganizationSubscription()
  const { isUiSetupShineInvoicingAccounting } = useOrganizationUiSetup()
  const { isInZervantNoPlan } = useZervantSubscriptionDetails()
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan>(SubscriptionPlan.Basic)
  const [isYearlyPayment, setIsYearlyPayment] = useState<boolean>(true)
  const [showProcessingScreen, setShowProcessingScreen] = useState(false)
  const [confirmPrice, setConfirmPrice] = useState<number>(0)
  const [recurringPrice, setRecurringPrice] = useState<number>(0)
  const [isPlansSelectionMode, setIsPlansSelectionMode] = useState(false)
  const prevIsPlansSelectionMode = useRef(isPlansSelectionMode)
  const { track } = useSegment()

  const [displaySubscriptionPeriodChangeModal, setDisplaySubscriptionPeriodChangeModal] = useState(false)

  const { monthlyPrice: currentPlanMonthlyPrice, yearlyPricePerMonth: currentPlanYearlyPricePerMonth } =
    useSubscriptionPlanPrices(subscriptionPlan)

  const { hash } = useLocation()

  const hashSearchParamsPart = (hash || '').split('?')[1]
  const params = qs.parse(hashSearchParamsPart, {
    ignoreQueryPrefix: true,
  })

  const {
    promocode,
    payment_cancelled: paymentCancelled,
    payment_failed: paymentFailed,
    payment_successful: paymentSuccessful,
    plan: signupCouponPlan,
  } = params

  const {
    plansSelectionMode: initialPlansSelectionMode = false,
    organizationId,
    onEditCardSubscription,
    onEditSubscription,
    onFetchOrganization,
  } = useOrganizationSubscriptionSettings()

  const {
    handleCouponEligibilityCheck,
    setDiscountPercent,
    totalPrice,
    totalRecurringPrice,
    calculatePriceWithCoupon,
  } = useCouponEligibility({
    organizationId,
    selectedPeriod: isYearlyPayment ? SubscriptionPeriod.Yearly : SubscriptionPeriod.Monthly,
    selectedPlan,
    confirmPrice,
    recurringPrice,
    signupCouponPlan: isValidSubscriptionPlan(signupCouponPlan) ? signupCouponPlan : undefined,
  })

  const { open: openPlanConfirmationModal, close: closeConfirmationModal } = useModal(ModalId.PlanConfirmationModal, {
    onClose: () => setDiscountPercent(0),
  })

  const queryClient = useQueryClient()

  const { data: subscription, isLoading: loadingSubscription } = useQuery(
    [QueryKeys.OrganizationSubscription, organizationId],
    () => fetchSubscription(organizationId),
    {
      enabled: !!organizationId,
    },
  )

  const hasPaymentMethod = () => !isEmpty(defaultActivePaymentMethod)

  const updateSubscriptionMutation = useMutation(
    ({ plan, coupon }: UpdateSubscriptionMutationParams) =>
      updateBillingSubscription(
        organizationId,
        getUpdateSubscriptionPayload(organizationId, plan, isYearlyPayment, coupon),
      ),
    {
      onSuccess: (data, { plan }) => {
        queryClient.invalidateQueries(QueryKeys.OrganizationSubscription)
        queryClient.invalidateQueries(QueryKeys.OrganizationSubscriptionInfo)

        setSelectedPlan(plan)
        handleGoToMainScreen()

        notifySubscriptionUpdateSuccess(t)
      },
      onError: (error) => {
        if (typeof error === 'object' && error !== null && 'message' in error) {
          notifySubscriptionUpdateError(t, (error as { message: string | undefined }).message)
        } else {
          notifySubscriptionUpdateError(t, undefined)
        }
      },
      onSettled: () => {
        setShowProcessingScreen(false)
      },
    },
  )

  const changeSubscriptionPeriodMutation = useMutation(
    (period: SubscriptionPeriod) => changeSubscriptionPeriod(organizationId, period),
    {
      onError: () => {
        setDisplaySubscriptionPeriodChangeModal(false)
        notify({
          id: NotificationKeys.OrganizationSubscriptionPeriodChange,
          message: t('organization_subscription.period_change.error'),
          variant: 'error',
        })
      },
    },
  )

  const handlePlanSelect = useCallback(
    (plan: SubscriptionPlan, period?: SubscriptionPeriod) => {
      setSelectedPlan(plan)
      setIsYearlyPayment(period === SubscriptionPeriod.Yearly)
      openPlanConfirmationModal()
      track('xxx - iam - pricing completed', {
        /* eslint-disable @typescript-eslint/naming-convention */
        plan_selected: plan,
        period_selected: period,
        /* eslint-enable @typescript-eslint/naming-convention */
      })
    },
    [openPlanConfirmationModal, track],
  )

  useCouponParams({ params, organizationId, handlePlanSelect })

  const isInUpdatePeriodState = useMemo(() => {
    return (
      (subscriptionPeriod === SubscriptionPeriod.Yearly && !displaySubscriptionPeriodChangeModal) ||
      (subscriptionPeriod === SubscriptionPeriod.Monthly && displaySubscriptionPeriodChangeModal)
    )
  }, [subscriptionPeriod, displaySubscriptionPeriodChangeModal])

  useEffect(() => {
    if (subscription && !isEmpty(subscription.organization)) {
      onFetchOrganization({ detail: subscription.organization })
    }
    // only trigger onFetchOrganization on subscription data change, not the props
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription])

  // If route redirects straight to plans selection screen
  useEffect(() => {
    setIsPlansSelectionMode(initialPlansSelectionMode)
  }, [initialPlansSelectionMode])

  // update previous state of plans selection mode flag to prevent event from firing multiple times
  useEffect(() => {
    prevIsPlansSelectionMode.current = isPlansSelectionMode
  }, [isPlansSelectionMode])

  const isPaymentCancelled = paymentCancelled === '1'
  const isPaymentFailed = paymentFailed === '1'
  const isPaymentSuccessful = paymentSuccessful === '1'

  useEffect(() => {
    if (isPaymentCancelled) {
      notify({
        variant: 'info',
        message: t('organization_subscription.payment_cancelled'),
        id: NotificationKeys.SubscriptionPaymentCancelled,
      })
    } else if (isPaymentFailed) {
      notify({
        variant: 'warning',
        message: t('organization_subscription.payment_failed'),
        id: NotificationKeys.SubscriptionPaymentFailed,
      })
    } else if (isPaymentSuccessful) {
      notify({
        variant: 'success',
        message: t('organization_subscription.card_added'),
        id: NotificationKeys.SubscriptionPaymentSuccessful,
      })
    }
  }, [isPaymentCancelled, isPaymentFailed, isPaymentSuccessful, t])

  const isShineWithFreeZervantPlan = isUiSetupShineInvoicingAccounting && isInZervantNoPlan

  const isViewLoading =
    (isShineWithFreeZervantPlan ? false : isSubscriptionDataLoading) || loadingSubscription || !subscription

  const handleGoToMainScreen = () => {
    navigate(SettingsRoute.OrganizationSubscription)
  }

  const handleEditSubscriptionRequest = (plan: SubscriptionPlan, price: number, coupon?: string) => {
    onEditSubscription({
      detail: {
        plan,
        period: isYearlyPayment ? SubscriptionPeriod.Yearly : SubscriptionPeriod.Monthly,
        price,
        coupon,
        forceExternal: isShineWithFreeZervantPlan,
        callback: (error) => {
          queryClient.invalidateQueries(QueryKeys.OrganizationSubscription)
          queryClient.invalidateQueries(QueryKeys.OrganizationSubscriptionInfo)
          closePlanConfirmation()
          setShowProcessingScreen(false)

          if (error) {
            notifySubscriptionUpdateError(t, error.message)
          } else {
            notifySubscriptionUpdateSuccess(t)
          }
        },
      },
    })
  }

  const handleNonPaidPlan = (plan: SubscriptionPlan, coupon?: string) => {
    updateSubscriptionMutation.mutate({ plan, coupon })
    closePlanConfirmation()
  }

  const handleEditSubscription = {
    [BillingType.External]: (plan: SubscriptionPlan, totalPrice: number, coupon?: string) => {
      if (isPaidPlan(plan) && (!subscription?.organization.activationDate || !hasPaymentMethod())) {
        handleEditSubscriptionRequest(plan, totalPrice, coupon)
      } else {
        handleNonPaidPlan(plan, coupon)
      }
    },
    [BillingType.Own]: (plan: SubscriptionPlan, _totalPrice: number, coupon?: string) => {
      // from this legacy-billing-system component, we always need to add new payment method when plan is paid
      // if user already has a payment method in Upodi, this component is not used anymore (then see apps/react-webapp/src/modules/organizationSubscription/Subscription.tsx)
      if (isPaidPlan(plan)) {
        handleEditSubscriptionRequest(plan, calculatePriceWithCoupon(confirmPrice), coupon)
      } else {
        handleNonPaidPlan(plan, coupon)
      }
    },
  }

  const handleConfirmPlanChange = (coupon?: string) => {
    setShowProcessingScreen(true)
    handleEditSubscription[billingType](selectedPlan, calculatePriceWithCoupon(confirmPrice), coupon)
  }

  const closePlanConfirmation = () => {
    closeConfirmationModal()
    setDiscountPercent(0)
  }

  const handleSubscriptionPeriodUpdate = () => {
    const switchPeriod =
      subscriptionPeriod === SubscriptionPeriod.Yearly ? SubscriptionPeriod.Monthly : SubscriptionPeriod.Yearly

    changeSubscriptionPeriodMutation.mutate(switchPeriod)

    setTimeout(() => {
      window.location.reload()
    }, 10000)
  }

  const toggleSubscriptionPeriod = useCallback(() => {
    setDisplaySubscriptionPeriodChangeModal(!displaySubscriptionPeriodChangeModal)
  }, [displaySubscriptionPeriodChangeModal])

  const handlePriceChange = useCallback((confirmPrice: number, recurringPrice: number) => {
    setConfirmPrice(confirmPrice)
    setRecurringPrice(recurringPrice)
  }, [])

  const shouldShowActivateAlert = isTrialNoPlanSelected && !hasPaymentMethod()
  const shouldShowSubscriptionOverview = !isViewLoading && !isPlansSelectionMode
  const shouldShowPlansOverlay = !isViewLoading && isPlansSelectionMode

  return (
    <>
      {shouldShowActivateAlert && <ActivateAlertComponent trialEndDate={planExpirationDate || ''} />}
      {(isSubscriptionUpdating || showProcessingScreen) && <ProcessingScreen />}
      {isViewLoading && <SkeletonBox height={385} fullWidth />}
      {shouldShowSubscriptionOverview && (
        <SubscriptionOverview
          isInUpdatePeriodState={isInUpdatePeriodState}
          isLoading={isViewLoading}
          onEditCardSubscription={onEditCardSubscription}
          onSubscriptionPeriodToggle={toggleSubscriptionPeriod}
          subscriptionPlans={subscription?.plans}
        />
      )}
      {shouldShowPlansOverlay && <SubscriptionPlansOverlay onPlanSelect={handlePlanSelect} />}
      <PlanConfirmationModal
        applyUpsellCoupon={promocode as string}
        id={ModalId.PlanConfirmationModal}
        onConfirm={handleConfirmPlanChange}
        onCouponEligibilityCheck={(coupon) => handleCouponEligibilityCheck(coupon)}
        onPriceChange={handlePriceChange}
        planText={t(`organization_subscription.${selectedPlan}`)}
        price={totalPrice}
        recurringPrice={totalRecurringPrice}
        subscriptionPeriod={isYearlyPayment ? SubscriptionPeriod.Yearly : SubscriptionPeriod.Monthly}
        subscriptionPlan={selectedPlan}
      />
      {displaySubscriptionPeriodChangeModal && (
        <SubscriptionPeriodChangeModal
          subscriptionPlan={subscriptionPlan}
          subscriptionPeriod={
            subscriptionPeriod === SubscriptionPeriod.Yearly ? SubscriptionPeriod.Monthly : SubscriptionPeriod.Yearly
          }
          price={
            subscriptionPeriod === SubscriptionPeriod.Yearly ? currentPlanMonthlyPrice : currentPlanYearlyPricePerMonth
          }
          onConfirm={handleSubscriptionPeriodUpdate}
          onClose={() => setDisplaySubscriptionPeriodChangeModal(!displaySubscriptionPeriodChangeModal)}
        />
      )}
    </>
  )
}

export default SubscriptionComponent
